import rp, { CodeEditor } from '@reporting/rp-app-host';
import { AppComponent, Saveable } from "@reporting/rp-app-host";
import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { ContextData, ReportContext } from '@reporting/rp-types/analytics';
import { style } from "./reportContainer.style";
import { AnalyticsApplication, ContextScriptCodeEditorAdapter, ReportErrorEvent, ToolbarItem } from "..";
import { Alert, ColorTypes, LayoutOptions, SplitContainer, actions, dom, utils } from "@omegagrid/core";
import { BaseVisualization, VisualizationOptions } from "./visualizations/baseVisualization";
import { confetti, ConfettiOptions } from "@tsparticles/confetti";
import { BaseVisualizationEditor, createVisualization, createVisualizationEditor, getVisualizationConfig } from "./visualizations";
import { msg } from "@omegagrid/localize";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { createRef, ref } from 'lit/directives/ref.js';
import { add, addItem as addDashboardItem, openContextJson, openContextSql, openReport, saveNew, saveReplace, getContext, getContextBroker, openContextFilter } from '../actions';
import { ReportChangeEvent, ReportStore } from '../model/reportStore';
import { cloneContext, isMultiContext } from '../common';

const DEFAULT_CONFETTI: ConfettiOptions = {
	angle: 90,
	count: 200,
	position: {
		x: 50,
		y: 50,
	},
	spread: 300,
	startVelocity: 45,
	decay: 0.9,
	gravity: 1,
	drift: 0,
	ticks: 200,
	colors: ["#5ec7d1"],
	shapes: ["square", "circle"],
	scalar: 1,
	zIndex: 100,
	disableForReducedMotion: true,
};

export class ReportContainerEvent extends Event {
	readonly container: ReportContainer;
	readonly reportContext: ReportContext;
	readonly module: ModuleCode;
	constructor(type: string, args?: Partial<ReportContainerEvent>) {
		super(`report.${type}`, {bubbles: true, composed: true});
		if (args) Object.assign(this, args);
	}

}

@customElement('rp-report-container')
export class ReportContainer extends AppComponent<AnalyticsApplication> implements Saveable {

	static styles = [style]

	@property({type: Number})
	itemIndex: number;

	/** stable runtime id of a dashboard item, alternative to itemIndex */
	@property({type: String})
	itemUid: string;

	@property({type: Object})
	reportContext: ReportContext;

	/** backing store of the context; set for dashboards, null for single reports */
	@property({type: Object})
	store: ReportStore;

	@property({type: Boolean})
	toolbar = false;

	@property({type: Object})
	confetti: ConfettiOptions | boolean = false;

	@property({ type: String })
	mode: 'view' | 'edit' | 'script' = 'view';

	@query('canvas')
	canvas: HTMLCanvasElement & {};

	@query('og-splitcontainer')
	splitContainer: SplitContainer;

	@state()
	leftToolbarItems: ToolbarItem[] = [];

	@state()
	rightToolbarItems: ToolbarItem[] = [];

	toolbarRef = createRef<HTMLDivElement>();
	get toolbarElm() { return this.toolbarRef?.value }

	bodyRef = createRef<HTMLDivElement>();
	_body: HTMLDivElement;
	get body() {
		if (this.bodyRef?.value) return this.bodyRef.value;
		if (this._body) return this._body;
		this._body = dom.createElement('div', { className: 'body' });
		return this._body;
	}

	_scriptEditor: CodeEditor;
	_scriptEditorAdapter: ContextScriptCodeEditorAdapter;
	get scriptEditor() {
		if (this._scriptEditor) return this._scriptEditor;
		this._scriptEditorAdapter = new ContextScriptCodeEditorAdapter(this.reportContext, () => this.visualization);
		this._scriptEditor = dom.createElement<CodeEditor>('rp-editor-code', {
			adapter: this._scriptEditorAdapter,
			closeButton: true,
			listeners: [['close', (e: Event) => {
				this.mode = 'view';
				e.stopPropagation();
			}]]
		});
		return this._scriptEditor;
	}

	_editor: BaseVisualizationEditor;
	async getEditor() {
		if (this._editor) return this._editor;
		this._editor = await createVisualizationEditor({context: this.reportContext});
		if (this._editor) {
			this._editor.result = this.visualization?.reportData;
			this._editor.visualization = this.visualization;
		}
		return this._editor;
	}

	get editorWidth() {
		if (this.mode == 'script') return null;
		return getVisualizationConfig(this.reportContext.context as ContextData)?.editorWidth ?? null;
	}

	/**
	 * Stable identity for the split container items - an inline array in the
	 * template would re-trigger the split container update on every container
	 * render, which re-attaches its components (resetting extension iframes).
	 * The split container mutates item.size when the user drags the splitter,
	 * so the rebuild compares against the last CONFIGURED width - comparing
	 * item.size would treat the drag as a config change and reset the width
	 * on the next render.
	 */
	private _splitItems: {id: string, size?: number}[];
	private _configuredEditorWidth: number|null;
	get splitItems() {
		if (!this._splitItems || this._configuredEditorWidth !== this.editorWidth) {
			this._configuredEditorWidth = this.editorWidth;
			this._splitItems = [{id: 'component'}, {id: 'sidebar', size: this.editorWidth}];
		}
		return this._splitItems;
	}


	visualization: BaseVisualization;
	loading = false;
	error: any;

	get service() { return rp.ms('analytics') }
	get contextData() { return this.reportContext?.context }

	private _confetti: Awaited<ReturnType<typeof confetti.create>>;
	private _confettiOptions: ConfettiOptions;
	private _options: VisualizationOptions;

	async startAnimation() {
		if (this._confettiOptions && this._confetti) this._confetti(this._confettiOptions);
		if (this.confetti === true) this._confettiOptions = DEFAULT_CONFETTI;
		if (this.confetti) this._confettiOptions = utils.mergeDeep({}, DEFAULT_CONFETTI, this.confetti as ConfettiOptions);
		if (!this._confettiOptions) return
		this._confetti = await confetti.create(this.canvas, this._confettiOptions);
		const container = await this._confetti(this._confettiOptions);
		setTimeout(() => container?.destroy(), 5000);
	}

	private _dirty = false;

	/** store-backed containers derive the dirty state, storeless reports track it via edit/save events */
	get isDirtyState() {
		return this.store ? this.store.isDirty : this._dirty;
	}

	constructor() {
		super();
		this.addEventListener('report.load', () => this.afterReportLoad());
		this.addEventListener('edit', () => {
			this._dirty = true;
			this._commitWorkingContext();
		});
		this.addEventListener('save', () => this._dirty = false);
	}

	/**
	 * Single reports work on a mutable working copy (visualizations and
	 * editors mutate contextData in place). Every edit event commits the
	 * working copy into the store, so the store draft is always the current
	 * state and the broker cache stays pristine. Committed only when the
	 * content actually changed (JSON comparison against the last commit).
	 */
	private _workingJson: string;
	private _commitWorkingContext() {
		if (!this.store || isMultiContext(this.reportContext)) return;
		const contextData = this.visualization?.contextData ?? this.reportContext?.context;
		if (!contextData) return;

		this._workingJson ??= JSON.stringify(this.store.contextData);
		const json = JSON.stringify(contextData);
		if (json === this._workingJson) return;
		this._workingJson = json;
		this.store.update(context => { context.context = JSON.parse(json) }, 'content');
	}

	/**
	 * Any store change means an edit (no matter whether it came from the
	 * dashboard, the editor or the sidebar filter) - the host dirty tracking
	 * is fed from this single place instead of per-call-site events.
	 */
	private _onStoreChange = (e: Event) => {
		if ((e as ReportChangeEvent).scope == 'reset') return;
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.toolbar) this.store?.addEventListener('change', this._onStoreChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.store?.removeEventListener('change', this._onStoreChange);
		// unsaved edits are discarded when the tab closes. Only for top-level
		// containers - dashboard items share the parent dashboard context.
		if (!this.toolbar) return;
		setTimeout(() => {
			if (this.isConnected) {
				// the component was only moved within the layout
				if (this.toolbar) this.store?.addEventListener('change', this._onStoreChange);
				return;
			}
			if (this.store) {
				// drop the store; the next open starts from the pristine broker cache
				rp.applications.get<AnalyticsApplication>('analytics').dropReportStore(this.store.idContext);
			} else if (this._dirty) {
				// single reports: drop the cached context so the next open
				// serves the saved version again
				getContextBroker().invalidateContext(this.reportContext);
				this._dirty = false;
			}
		});
	}

	afterReportLoad() {
		this.afterVisualizationLoaded();
		this.startAnimation();
	}

	async afterVisualizationLoaded() {
		this._options = this.visualization && this.visualization.getOptions ? await this.visualization.getOptions() : {
			refresh: true, // enabled refresh even if visualization load fails
		};

		this.leftToolbarItems = this.createLeftToolbarItems();
		this.rightToolbarItems = this.createRightToolbarItems();
		this.loading = false;
		this.hideLoader();
	}

	handleError = (error: any) => {
		this.error = error;
		rp.log.error(error);
		dom.empty(this.body);
		const alert = dom.createElement<Alert>('og-alert');
		alert.style.margin = '10px';
		alert.type = 'danger';
		alert.text = error?.message ? msg(error.message) : msg(`Report loading error`);
		this.body.appendChild(alert);
	}

	/**
	 * Open a different context in this container (the host replace flow
	 * reuses the existing container instance instead of creating a new one).
	 * Resets all per-report state - store wiring, working copy, dirty
	 * tracking, edit mode - exactly as if the container was newly created.
	 */
	async openContext(reportContext: ReportContext, store: ReportStore) {
		this.store?.removeEventListener('change', this._onStoreChange);
		if (this.toolbar && this.store && this.store !== store) {
			// this container owned the previous report; unsaved changes are
			// discarded with the tab content (same as closing it)
			rp.applications.get<AnalyticsApplication>('analytics').dropReportStore(this.store.idContext);
		}
		this.store = store ?? null;
		if (this.toolbar) this.store?.addEventListener('change', this._onStoreChange);

		this.reportContext = store
			? (isMultiContext(store.context) ? store.context : cloneContext(store.context))
			: reportContext;

		this.itemIndex = null;
		this.itemUid = null;
		this._workingJson = null;
		this._dirty = false;
		this.error = null;
		this.visualization = null;
		this.mode = 'view';

		// a load of the previous context may still be in flight - the new
		// context must win, so this load supersedes it instead of being
		// skipped by the loading guard
		await this.loadReport(false, true);
	}

	/**
	 * Sequence number of the latest load. A load that finishes with a stale
	 * number was superseded by openContext() meanwhile; its result is
	 * dropped so it cannot overwrite the newer context.
	 */
	private _loadSeq = 0;

	loadReport = async (reloadContext = false, supersede = false) => {
		if (this.loading && !supersede) return;
		const seq = ++this._loadSeq;
		this.showLoader();
		this.loading = true;
		this.error = null;

		try {
			if (reloadContext && this.store) {
				// replace the store snapshots with the fresh server version;
				// unsaved changes are discarded (same as before the store)
				getContextBroker().invalidateContext(this.reportContext);
				const fresh = await getContextBroker().getContext({id_context: this.store.idContext});
				if (seq !== this._loadSeq) return;
				this.store.resetTo(fresh);
				this.reportContext = isMultiContext(this.store.context)
					? this.store.context
					: cloneContext(this.store.context);
				this._workingJson = null;
				reloadContext = false;
			}

			const visualization = await createVisualization({
				context: this.reportContext,
				itemIndex: this.itemIndex,
				itemUid: this.itemUid,
				reloadContext
			});
			if (seq !== this._loadSeq) return;

			this.visualization = visualization;
			if (!this.visualization) {
				this.visualization = dom.createElement('div');
			} else if (this._editor) {
				this._editor.result = this.visualization.reportData;
				this._editor.visualization = this.visualization;
			}

			this.visualization.addEventListener('report.error', (e: ReportErrorEvent) => {
				if (this.loading) this.afterReportLoad();
				this.handleError(e.error);
			});

			dom.empty(this.body);
			this.body.appendChild(this.visualization);
		} catch (error) {
			if (seq !== this._loadSeq) return;
			this.visualization = null;
			this.handleError(error);
		} finally {
			// a superseded load leaves the loader/toolbar to the newer one
			if (seq === this._loadSeq) this.afterVisualizationLoaded();
		}
	}

	previewReport = async (contextData: Partial<ContextData>) => {
		const previewContextData = utils.mergeDeep({} as ContextData, contextData, this.contextData as ContextData);
		this.loading = true;
		const visualization = await createVisualization({context: previewContextData});
		this.loading = false;
		dom.empty(this.body);
		this.hideLoader();
		this.body.appendChild(visualization);
	}

	resetReport = async () => {
		if (this.visualization) {
			dom.empty(this.body);
			this.body.appendChild(this.visualization);
		} else {
			await this.loadReport();
		}
	}

	private _externalTitle: HTMLElement;
	private _externalTitleText: string;

	layout(options?: LayoutOptions) {
		if (options?.elements?.has('title')) {
			this._externalTitle = options.elements.get('title');
			this.initToolbar();
		}

		if (!this.visualization?.layout) return;

		if (this.splitContainer) {
			setTimeout(() => this.visualization.layout(), 1);
		} else {
			this.visualization?.layout({
				parent: this,
				elements: new Map(this.toolbarElm ? [['toolbar', this.toolbarElm]] : []),
			});
		}
	}

	private initToolbar() {
		if (!this.toolbarElm) return;
		if (!this._externalTitle) return;
		if (this.toolbarElm.parentElement == this._externalTitle) return
		if (this._externalTitleText == null) this._externalTitleText = this._externalTitle.innerText || '';

		const title = this.toolbarElm.querySelector<HTMLDivElement>('.title');
		if (title && this._externalTitleText) title.innerText = this._externalTitleText;

		dom.empty(this._externalTitle);
		this._externalTitle.appendChild(this.toolbarElm);
	}

	willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('mode')) {
			this._body = null;
			// some toolbar items depend on the edit mode
			this.leftToolbarItems = this.createLeftToolbarItems();
			this.rightToolbarItems = this.createRightToolbarItems();
		}
		if (props.has('reportContext')) this._editor = null;
	}

	async updated() {
		this.initToolbar();
		if (!this.visualization && !this.error && this.reportContext) await this.loadReport();
		if (this.visualization && this.visualization.parentElement != this.body) {
			dom.empty(this.body);
			this.body.appendChild(this.visualization);
		}
		this.layout();
	}

	async save(replace = false) {
		if (this.mode == 'script') this.scriptEditor?.save();

		// flush edits the visualization holds outside the store (e.g. the
		// debounced sheet commits of a spreadsheet)
		(this.visualization as {commitEdits?: () => void})?.commitEdits?.();

		let reportContext: ReportContext;
		if (this.store) {
			// make sure the latest working copy state is in the store draft
			this._commitWorkingContext();
			reportContext = cloneContext(this.store.context);
		} else {
			this.reportContext.context = this.visualization.contextData;
			reportContext = cloneContext(this.reportContext);
		}

		const oldId = reportContext.id_context;
		const newReportContext = await (replace ? saveReplace(reportContext) : saveNew(reportContext));
		if (!newReportContext) return;
		const newId = newReportContext.id_context;

		if (this.store) {
			this.store.markSaved(newReportContext);
			if (isMultiContext(this.reportContext)) {
				this.reportContext = this.store.context;
			} else {
				// keep the working copy (the visualization and the editor hold
				// it), only adopt the saved metadata (id, hash, name, ...)
				const {context: _, ...savedMeta} = this.store.context;
				Object.assign(this.reportContext, savedMeta);
				this._workingJson = null;
			}
		}

		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
		rp.host.updateComponent(this.componentId ?? `analytics-reportContainer-${oldId}`, {
			id: `analytics-reportContainer-${newId}`,
			title: newReportContext.name || `#${newId}`,
		});
		// the host rename fires no activate event - sync the store registry,
		// the active id and the sidebar filter by hand
		rp.applications.get<AnalyticsApplication>('analytics').renameReportContainer(oldId, newId);
	}

	async edit() {
		this.visualization?.edit();
		if (this.mode == 'edit') {
			this.mode = 'view';
		} else {
			const editor = await this.getEditor();
			this.mode = editor ? 'edit' : 'view';

		}
	}

	async addTo(module: 'S'|'D') {
		const targetContext = await add(this.reportContext, module);
		if (targetContext) {
			// the target dashboard may be open: adopt the saved server version
			// (an unsaved draft of the target is replaced, same as before)
			rp.applications.get<AnalyticsApplication>('analytics')
				.getReportStore(targetContext.id_context)?.resetTo(targetContext);

			const container = rp.applications.get<AnalyticsApplication>('analytics')
				.getReportContainer(targetContext.id_context);
			if (container) {
				container.loadReport();
			} else {
				openReport(targetContext.id_context, {
					target: 'new_tab',
					title: targetContext.name,
					context: targetContext,
				});
			}
		}
	}

	async addItem(type: string) {
		// the store 'structure' change event reloads the dashboard, fixes
		// collisions and feeds the dirty tracking - nothing to sync by hand
		await addDashboardItem(type, this.store);
	}

	createLeftToolbarItems() {
		const items: ToolbarItem[] = [];
		if (!this.toolbar || !this._options) return items;

		if (this._options.save) {
			const list = this._options.addTo?.length || this._options.saveAs;
			items.push({
				type: list ? 'dropdown-menu' : 'button',
				id: 'menu-save',
				width: 25,
				title: msg('Save'),
				color: ColorTypes.Transparent,
				icon: {icon: 'save'},
				options: { noCaret: true },
				alignment: 'left',
				items: list ? [
					{k: 'save', v: msg('Save') + ` (${msg('new')})`, icon: {icon: 'save'}},
					{k: 'saveReplace', v: msg('Save') + ` (${msg('replace')})`, icon: {icon: 'save'}},
					...this._options.addTo?.map(type => ({
						k: `addTo-${type}`,
						v: msg(`Add to ${type}`),
						icon: {icon: 'plus'},
					}))
				] : null
			});
		}

		if (this._options.refresh) {
			items.push({
				type: 'button',
				id: 'refresh',
				width: 25,
				color: ColorTypes.Transparent,
				title: msg('Refresh'),
				icon: {icon: 'rotate-right'},
			});
		}

		items.push({
			type: 'button',
			id: 'filter',
			width: 25,
			color: ColorTypes.Transparent,
			title: msg('Filter'),
			icon: {icon: 'filter'},
		});

		if (this._options.actions?.length) {
			this._options.actions.forEach(action => items.push({
				type: 'button',
				text: action.text,
				id: `action-${action.id}`,
				icon: action.icon,
				color: action.color || ColorTypes.Transparent,
			}));
		}

		return items;
	}

	createRightToolbarItems() {
		const items: ToolbarItem[] = [];
		if (!this.toolbar || !this._options) return items;

		if (this._options.exportTypes?.length) {
			items.push({
				type: 'dropdown-menu',
				id: 'export',
				text: 'Export',
				width: 90,
				icon: {icon: 'download'},
				items: this._options.exportTypes?.map(type => ({
					k: `export-${type.type}`,
					v: msg(type.label ?? `Export to ${type.type}`),
					icon: type.icon ?? 'download',
				}))
			});
		}

		if (this._options.addMenu?.length && this.mode == 'edit') {
			items.push({
				type: 'dropdown-menu',
				id: 'menu-add',
				width: 25,
				color: ColorTypes.Transparent,
				title: msg('Add'),
				icon: {icon: 'plus'},
				options: {noCaret: true},
				alignment: 'right',
				items: this._options.addMenu.map(action => ({
					k: `add-${action.id}`,
					v: action.text,
					icon: action.icon,
				}))
			});
		}

		if (this._options.edit) {
			const editing = this.mode == 'edit';
			items.push({
				type: 'button',
				id: 'edit',
				width: editing ? 60 : 25,
				color: editing ? ColorTypes.Accent : ColorTypes.Transparent,
				icon: {icon: 'pen-ruler'},
				text: editing ? msg('Close') : null,
				title: msg('Edit'),
			});
		}

		if (this.reportContext) {
			items.push({
				id: 'link',
				type: 'button',
				width: 30,
				color: ColorTypes.Transparent,
				icon: {icon: 'link'},
				title: msg('Copy view link'),
			});
		}

		if (this.reportContext) {
			items.push({
				id: 'code',
				type: 'dropdown-menu',
				width: 30,
				color: ColorTypes.Transparent,
				icon: {icon: 'code'},
				options: {noCaret: true},
				title: msg('Sources'),
				items: [
					{k: 'scriptMode', v: 'Scripts', icon: 'scroll'},
					{k: 'openJson', v: 'JSON', icon: 'file-brackets-curly'},
					...(rp.userProvider.hasRight('etl') ? [{k: 'openSql', v: 'SQL', icon: 'database'}] : []),
				]
			});
		}

		return items;
	}

	export(type: string) {
		this.visualization?.download(type);
	}

	refresh() {
		this.loadReport(true);
		this.dispatchEvent(new CustomEvent('save', {bubbles: true, composed: true}));
	}

	_onToolbarAction = async (e: actions.ActionEvent) => {
		if (e.action.type == 'select') {
			switch (e.action.key) {
				case 'save': this.save(); break;
				case 'saveReplace': this.save(true); break;
				case 'addTo-dashboard': this.addTo('D'); break;
				case 'addTo-spreadsheet': this.addTo('S'); break;
				case 'refresh': this.refresh(); break;
				case 'openJson': openContextJson(this.reportContext); break;
				case 'openSql': openContextSql(this.reportContext); break;
				case 'scriptMode': this.mode = 'script'; break;
				default:
					if (/^export-/.test(e.action.key)) this.export(e.action.key.replace(/^export-/, ''));
					else if (/^add-/.test(e.action.key)) this.addItem(e.action.key.replace(/^add-/, ''));
			}
		} else {
			switch (e.action.key) {
				case 'refreshScript': this.scriptEditor.save(); break;
				case 'stopScriptEdit': this.mode = 'view'; break;
				case 'edit': this.edit(); break;
				case 'refresh': this.refresh(); break;
				case 'filter': openContextFilter(this.reportContext.id_context); break;
				case 'link':
					const url = await this.getDirectLink();
					navigator.clipboard.writeText(url);
					rp.host.showAlert({
						type: 'info',
						title: msg('Views'),
						content: msg('Copied to clipboard: {v}', {data: {v: url}}),
					});
					break;
				default:
					if (/^action-/.test(e.action.key)) {
						const actionId = e.action.key.replace(/^action-/, '');
						this.visualization?.invokeAction(actionId);
					}
			}
		}
	}

	async getDirectLink() {
		if (!this.reportContext.id_context) {
			this.reportContext = await getContext(this.reportContext);
		}
		const url = new URL(window.location.protocol + '//' + window.location.host);
		url.pathname = `${this.service.a}/analytics/${this.reportContext.id_context}`;
		return url.toString();
	}

	_createComponent = (id: string) => {
		if (id == 'component') return this.body;
		else if (this.mode == 'script') return this.scriptEditor;
		else return this.getEditor();
	}

	render = () => html`
		<div class="container">
			${this.leftToolbarItems.length || this.rightToolbarItems.length ? html`
				<!-- layout via inline styles: the element may be moved into an external
					title container (initToolbar), where shadow DOM styles do not apply -->
				<div class="toolbar" ${ref(this.toolbarRef)}
					style="display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; overflow: hidden">
					<div class="toolbar-side" style="display: flex; align-items: center; height: 100%">
						${this.leftToolbarItems.length ? html`
							<og-toolbar
								style="background-color: transparent; flex: 0 0 auto"
								.items="${this.leftToolbarItems}"
								@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
							</og-toolbar>
						` : ''}

						<div class="toolbar-left" style="flex: 1 1 auto; display: flex; align-items: center; height: 100%"></div>
					</div>

					<div style="min-width: 0; overflow: hidden">
						<div class="title" style="padding: 0 8px; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"></div>
					</div>

					<div class="toolbar-side" style="display: flex; align-items: center; justify-content: flex-end; height: 100%">
						<div class="toolbar-right" style="flex: 1 1 auto; display: flex; align-items: center; justify-content: flex-end; height: 100%"></div>

						${this.rightToolbarItems.length ? html`
							<og-toolbar
								style="background-color: transparent; flex: 0 0 auto"
								.items="${this.rightToolbarItems}"
								@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
							</og-toolbar>
						` : ''}
					</div>
				</div>
			` : ''}

			${this.mode == 'view' ? html`
				<div class="body" ${ref(this.bodyRef)}></div>
			` : html`
				<og-splitcontainer
					orientation="horizontal"
					.items="${this.splitItems}"
					.createComponent="${this._createComponent}"
					orientation="vertical"
					style="height: 100%">
				</og-splitcontainer>
			`}
		</div>

		<canvas id="canvas-${Date.now()}-${Math.random().toString(36).substring(2, 9)}"></canvas>
	`;

}
